import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import Spinner from "components/ui/Spinner/Spinner";
import ConfirmDialog from "components/ui/ConfirmDialog/ConfirmDialog";
import { useI18n } from "context/I18nContext";
import {
  createCard,
  deleteCard,
  fetchCardDetail,
  fetchCardLookups,
  updateCard,
  type CardDetail,
  type CardSavePayload,
  type CardStatusLookupDto,
} from "utils/cardsApi";
import { ApiError } from "utils/apiClient";
import {
  validateAccountByNumber,
  type AccountValidationResponse,
  lookupAccount,
  type AccountLookupResponse,
} from "utils/accountsApi";
import {
  formatDetailLabel,
  getDetailEntries,
  normaliseErrorDetail,
  type ErrorDetail,
} from "utils/errorDetails";
import styles from "./CardFormPage.module.scss";

type CardFormMode = "create" | "edit";

type CardFormPageProps = {
  mode: CardFormMode;
};

type RouteState = {
  from?: {
    pathname?: string;
    search?: string;
  };
  flash?: {
    variant: "success" | "error";
    message: string;
  };
};

type CardFieldErrors = Partial<
  Record<"cardNumber" | "referenceId" | "issueDate" | "expireDate", string>
>;

const formatDateInput = (value?: string | null) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.slice(0, 10);
  }
  return date.toISOString().slice(0, 10);
};

type LookupParams = {
  accountNo?: string;
  mobileNo?: string;
  cardNumber?: string;
};

const inferLookupParams = (raw: string): LookupParams => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {};
  }

  if (/^\d{11}$/.test(trimmed)) {
    return { mobileNo: trimmed };
  }

  if (/^\d+$/.test(trimmed) && trimmed.length >= 6) {
    return { cardNumber: trimmed };
  }

  return { accountNo: trimmed };
};

type StatusOption = { id: number; name: string };

const normaliseStatusOptions = (options?: CardStatusLookupDto[]): StatusOption[] =>
  options?.map(({ id, name }) => ({ id, name: name ?? String(id) })) ?? [];

function CardFormPage({ mode }: CardFormPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = mode === "edit";
  const { t } = useI18n();
  const routeState = location.state as RouteState | undefined;
  const redirectTimerRef = useRef<number | null>(null);
  const alertStackRef = useRef<HTMLDivElement | null>(null);
  const cardNumberRef = useRef<HTMLInputElement | null>(null);
  const referenceIdRef = useRef<HTMLInputElement | null>(null);
  const issueDateRef = useRef<HTMLInputElement | null>(null);
  const expireDateRef = useRef<HTMLInputElement | null>(null);

  const backTarget = useMemo(
    () => ({
      pathname: routeState?.from?.pathname ?? "/cards",
      search: routeState?.from?.search ?? "",
    }),
    [routeState?.from?.pathname, routeState?.from?.search],
  );

  const [formState, setFormState] = useState({
    cardNumber: "",
    referenceId: "",
    statusId: "",
    issueDate: "",
    expireDate: "",
  });
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
  const [linkedAccount, setLinkedAccount] = useState<AccountValidationResponse | null>(null);
  const [accountInput, setAccountInput] = useState("");
  const [alternateInput, setAlternateInput] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveDetail, setSaveDetail] = useState<ErrorDetail>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [postSaveLock, setPostSaveLock] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<CardFieldErrors>({});

  useEffect(
    () => () => {
      if (redirectTimerRef.current) {
        window.clearTimeout(redirectTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!(loadError || saveError || saveSuccess || deleteError)) {
      return;
    }
    const element = alertStackRef.current;
    if (!element) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [loadError, saveError, saveSuccess, deleteError]);

  useEffect(() => {
    if (!isEditMode || !id) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const detail: CardDetail = await fetchCardDetail(id, controller.signal, {
          includeLookups: true,
        });
        setFormState({
          cardNumber: detail.cardNumber,
          referenceId: detail.referenceId ?? "",
          statusId: detail.statusId ? String(detail.statusId) : "",
          issueDate: formatDateInput(detail.issueDate),
          expireDate: formatDateInput(detail.expireDate),
        });
        setStatusOptions(normaliseStatusOptions(detail.lookups?.statuses));
        if (detail.accountId && detail.accountNo) {
          setLinkedAccount({
            accountId: detail.accountId,
            accountNo: detail.accountNo,
            customerName: detail.accountName ?? undefined,
            walletBalance: detail.walletBalance ?? undefined,
          });
          setAccountInput(detail.accountNo);
        }
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        setLoadError((err as Error).message || t("cardForm.errors.loadFailed"));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [id, isEditMode, t]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }
    const controller = new AbortController();
    const loadLookups = async () => {
      try {
        const result = await fetchCardLookups(controller.signal);
        setStatusOptions(normaliseStatusOptions(result?.statuses));
      } catch {
        setStatusOptions([]);
      }
    };
    loadLookups();
    return () => controller.abort();
  }, [isEditMode]);

  useEffect(() => {
    const defaultStatusId = statusOptions[0]?.id;
    if (!defaultStatusId) {
      return;
    }
    setFormState((previous) => {
      if (previous.statusId) {
        return previous;
      }
      return {
        ...previous,
        statusId: String(defaultStatusId),
      };
    });
  }, [statusOptions]);

  const sectionTitle = isEditMode ? t("cardForm.titleEdit") : t("cardForm.titleCreate");
  const sectionSubtitle = isEditMode
    ? t("cardForm.subtitleEdit")
    : t("cardForm.subtitleCreate");
  const primaryButtonLabel = isEditMode
    ? t("cardForm.buttons.save")
    : t("cardForm.buttons.create");
  const primaryBusyLabel = isEditMode
    ? t("cardForm.buttons.saving")
    : t("cardForm.buttons.creating");

  const navigateBack = () =>
    navigate(
      { pathname: backTarget.pathname, search: backTarget.search },
      { replace: false },
    );

  const isTrackableField = (
    name: string,
  ): name is Exclude<keyof CardFieldErrors, "account"> =>
    name === "cardNumber" ||
    name === "referenceId" ||
    name === "issueDate" ||
    name === "expireDate";

  const clearFieldError = (field: keyof CardFieldErrors) => {
    setFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    if (isTrackableField(name)) {
      clearFieldError(name);
    }
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAccountInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAccountInput(event.target.value);
    setLookupError(null);
  };

  const handleAlternateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAlternateInput(event.target.value);
    setLookupError(null);
  };

  const handleVerifyAccount = async () => {
    const trimmed = accountInput.trim();
    if (!trimmed) {
      setLookupError(t("cardForm.errors.identifierRequired"));
      return;
    }
    setLookupError(null);
    setLookupLoading(true);
    try {
      const result = await validateAccountByNumber(trimmed);
      setLinkedAccount(result);
      setAccountInput(result.accountNo);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      setLookupError(apiError?.message || (err as Error).message || t("cardForm.errors.lookupFailed"));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAlternateLookup = async () => {
    const trimmed = alternateInput.trim();
    if (!trimmed) {
      setLookupError(t("cardForm.errors.identifierRequired"));
      return;
    }
    setLookupError(null);
    setLookupLoading(true);
    try {
      const params = inferLookupParams(trimmed);
      const lookup: AccountLookupResponse = await lookupAccount(params);
      const accountId = lookup.account?.id ?? null;
      const accountNo = lookup.account?.accountNo ?? "";
      if (!accountId || !accountNo) {
        throw new Error(t("cardForm.errors.lookupFailed"));
      }
      const linked: AccountValidationResponse = {
        accountId,
        accountNo,
        customerName: lookup.customer?.customerName ?? undefined,
        walletBalance: lookup.account?.walletBalance ?? undefined,
      };
      setLinkedAccount(linked);
      setAccountInput(linked.accountNo);
      setAlternateInput("");
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      setLookupError(apiError?.message || (err as Error).message || t("cardForm.errors.lookupFailed"));
    } finally {
      setLookupLoading(false);
    }
  };

useEffect(() => {
  setFieldErrors((previous) => {
    let changed = false;
    const next = { ...previous };
    (["issueDate", "expireDate"] as const).forEach((key) => {
      if (next[key]) {
        delete next[key];
        changed = true;
      }
    });
    return changed ? next : previous;
  });
}, [isEditMode]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError(null);
    setSaveDetail(null);
    setSaveSuccess(null);
    const trimmedCardNumber = formState.cardNumber.trim();
    const trimmedReferenceId = formState.referenceId.trim();
    const statusId = Number(formState.statusId || 0);
    const validationErrors: CardFieldErrors = {};
    if (!trimmedCardNumber) {
      validationErrors.cardNumber = t("cardForm.errors.cardNumberRequired");
    }
    if (!trimmedReferenceId) {
      validationErrors.referenceId = t("cardForm.errors.referenceIdRequired");
    }
    // Issue and expiry dates are now optional.
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      const focusOrder: Array<keyof CardFieldErrors> = ["cardNumber", "referenceId"];
      for (const field of focusOrder) {
        if (!validationErrors[field]) {
          continue;
        }
        const target =
          field === "cardNumber"
            ? cardNumberRef.current
            : field === "referenceId"
              ? referenceIdRef.current
              : field === "issueDate"
                ? issueDateRef.current
                : field === "expireDate"
                  ? expireDateRef.current
                  : null;
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          target.focus();
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
        break;
      }
      return;
    }
    setFieldErrors({});
    if (!statusId) {
      setSaveError(t("cardForm.errors.saveFailed"));
      return;
    }

    const payload: CardSavePayload = {
      cardNumber: trimmedCardNumber,
      referenceId: trimmedReferenceId,
      statusId,
      issueDate: formState.issueDate || undefined,
      expireDate: formState.expireDate || undefined,
      accountId: linkedAccount?.accountId ?? null,
    };

    setSaving(true);
    setPostSaveLock(false);
    try {
      if (isEditMode && id) {
        await updateCard(id, payload);
        setSaveSuccess(t("cardForm.messages.updateSuccess"));
      } else {
        await createCard(payload);
        setSaveSuccess(t("cardForm.messages.createSuccessRedirect"));
        setPostSaveLock(true);
        redirectTimerRef.current = window.setTimeout(() => {
          navigate(
            { pathname: backTarget.pathname, search: backTarget.search },
            {
              replace: true,
              state: {
                flash: {
                  variant: "success",
                  message: t("cardsPage.flash.createSuccess"),
                },
                from: backTarget,
              },
            },
          );
        }, 1500);
      }
    } catch (err) {
      const apiError = err instanceof ApiError ? err : null;
      setSaveDetail(normaliseErrorDetail(apiError?.detail));
      setSaveError(apiError?.message || (err as Error).message || t("cardForm.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteCard(id);
      navigate(
        { pathname: backTarget.pathname, search: backTarget.search },
        {
          replace: true,
          state: {
            flash: {
              variant: "success",
              message: t("cardsPage.flash.deleteSuccess"),
            },
            from: backTarget,
          },
        },
      );
    } catch (err) {
      setDeleteError((err as Error).message || t("cardForm.errors.deleteFailed"));
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  const detailEntries = getDetailEntries(saveDetail);
  const canSubmit =
    Boolean(formState.cardNumber.trim()) &&
    Boolean(formState.referenceId.trim()) &&
    Number(formState.statusId || 0) > 0;

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div className={styles.intro}>
            <h1 className={styles.title}>{sectionTitle}</h1>
            <p className={styles.subtitle}>{sectionSubtitle}</p>
          </div>
          <button
            type="button"
            className={`${styles.backButton} ${styles.backButtonSmall}`}
            onClick={navigateBack}
            disabled={saving || deleting}
          >
            {t("cardForm.buttons.back")}
          </button>
        </div>

        {deleteError && (
          <Alert variant="error" dismissible onClose={() => setDeleteError(null)}>
            {deleteError}
          </Alert>
        )}

        {loadError && (
          <Alert variant="error" dismissible onClose={() => setLoadError(null)}>
            {loadError}
          </Alert>
        )}

        {(saveError || saveSuccess) && (
          <div ref={alertStackRef} className={styles.alertStack}>
            {saveError && (
              <Alert variant="error" dismissible onClose={() => setSaveError(null)}>
                <div>{saveError}</div>
                {detailEntries.length > 0 && (
                  <ul className={styles.detailList}>
                    {detailEntries.map(([key, value]) => (
                      <li key={key} className={styles.detailItem}>
                        <strong>{formatDetailLabel(key)}:</strong> {String(value)}
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}
            {saveSuccess && (
              <Alert variant="success" dismissible onClose={() => setSaveSuccess(null)}>
                {saveSuccess}
              </Alert>
            )}
          </div>
        )}

        {loading ? (
          <div className={styles.loadingState}>
            <Spinner label={t("cardForm.loading")} />
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <fieldset className={styles.fieldset} disabled={saving || postSaveLock}>
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>{t("cardForm.sections.card")}</h2>
                <div className={styles.fieldGrid}>
                  <label className={styles.field} htmlFor="cardNumber">
                    <span className={styles.label}>
                      {t("cardForm.fields.cardNumber")}
                      <span className={styles.required} aria-hidden="true">*</span>
                    </span>
                    <input
                      ref={cardNumberRef}
                      id="cardNumber"
                      name="cardNumber"
                      className={styles.input}
                      type="text"
                      value={formState.cardNumber}
                      onChange={handleFieldChange}
                      placeholder={t("cardForm.placeholders.cardNumber")}
                      disabled={postSaveLock}
                    />
                    {fieldErrors.cardNumber && (
                      <span className={styles.fieldError} role="alert">
                        {fieldErrors.cardNumber}
                      </span>
                    )}
                  </label>

                  <label className={styles.field} htmlFor="referenceId">
                    <span className={styles.label}>
                      {t("cardForm.fields.referenceId")}
                      <span className={styles.required} aria-hidden="true">*</span>
                    </span>
                    <input
                      ref={referenceIdRef}
                      id="referenceId"
                      name="referenceId"
                      className={styles.input}
                      type="text"
                      value={formState.referenceId}
                      onChange={handleFieldChange}
                      placeholder={t("cardForm.placeholders.referenceId")}
                    />
                    {fieldErrors.referenceId && (
                      <span className={styles.fieldError} role="alert">
                        {fieldErrors.referenceId}
                      </span>
                    )}
                  </label>

                  <label className={styles.field} htmlFor="statusId">
                    <span className={styles.label}>{t("cardForm.fields.status")}</span>
                    <select
                      id="statusId"
                      name="statusId"
                      className={styles.select}
                      value={formState.statusId}
                      onChange={handleFieldChange}
                      disabled={!statusOptions.length || postSaveLock}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.id} value={String(option.id)}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  {isEditMode && (
                    <label className={styles.field} htmlFor="issueDate">
                      <span className={styles.label}>{t("cardForm.fields.issueDate")}</span>
                      <input
                        ref={issueDateRef}
                        id="issueDate"
                        name="issueDate"
                        className={styles.input}
                        type="date"
                        value={formState.issueDate}
                        onChange={handleFieldChange}
                        placeholder={t("cardForm.placeholders.issueDate")}
                      />
                      {fieldErrors.issueDate && (
                        <span className={styles.fieldError} role="alert">
                          {fieldErrors.issueDate}
                        </span>
                      )}
                    </label>
                  )}

                  <label className={styles.field} htmlFor="expireDate">
                    <span className={styles.label}>
                      {t("cardForm.fields.expireDate")}
                    </span>
                    <input
                      ref={expireDateRef}
                      id="expireDate"
                      name="expireDate"
                      className={styles.input}
                      type="date"
                      value={formState.expireDate}
                      onChange={handleFieldChange}
                      placeholder={t("cardForm.placeholders.expireDate")}
                    />
                    {fieldErrors.expireDate && (
                      <span className={styles.fieldError} role="alert">
                        {fieldErrors.expireDate}
                      </span>
                    )}
                  </label>
                </div>
              </div>
            </fieldset>

            <fieldset className={styles.fieldset} disabled={saving || postSaveLock}>
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h2 className={styles.sectionTitle}>{t("cardForm.sections.account")}</h2>
                </div>
                <div className={styles.lookupGrid}>
                  <label className={styles.field} htmlFor="accountNo">
                    <span className={styles.label}>{t("cardForm.fields.accountNo")}</span>
                    <div className={styles.inlineField}>
                      <input
                        id="accountNo"
                        className={styles.input}
                        type="text"
                        value={accountInput}
                        onChange={handleAccountInputChange}
                        placeholder={t("cardForm.placeholders.accountNo")}
                      />
                      <button
                        type="button"
                        className={styles.verifyButton}
                        onClick={handleVerifyAccount}
                        disabled={lookupLoading || !accountInput.trim()}
                      >
                        {lookupLoading
                          ? t("cardForm.buttons.verifying")
                          : t("cardForm.buttons.verify")}
                      </button>
                    </div>
                  </label>

                  <label className={styles.field} htmlFor="alternateLookup">
                    <span className={styles.label}>{t("cardForm.fields.alternateLookup")}</span>
                    <div className={styles.inlineField}>
                      <input
                        id="alternateLookup"
                        className={styles.input}
                        type="text"
                        value={alternateInput}
                        onChange={handleAlternateInputChange}
                        placeholder={t("cardForm.placeholders.alternateLookup")}
                      />
                      <button
                        type="button"
                        className={styles.lookupButton}
                        onClick={handleAlternateLookup}
                        disabled={lookupLoading || !alternateInput.trim()}
                      >
                        {lookupLoading
                          ? t("cardForm.buttons.lookingUp")
                          : t("cardForm.buttons.lookup")}
                      </button>
                    </div>
                    <small className={styles.helperText}>
                      {t("cardForm.messages.lookupHint")}
                    </small>
                  </label>
                </div>
                {lookupError && <p className={styles.errorText}>{lookupError}</p>}
                {linkedAccount && (
                  <div className={styles.previewCard}>
                    <div>
                      <span className={styles.previewLabel}>
                        {t("cardForm.linkedPreview.customer")}
                      </span>
                      <span className={styles.previewValue}>
                        {linkedAccount.customerName || "—"}
                      </span>
                    </div>
                    <div>
                      <span className={styles.previewLabel}>
                        {t("cardForm.linkedPreview.account")}
                      </span>
                      <span className={styles.previewValue}>{linkedAccount.accountNo}</span>
                    </div>
                    <div>
                      <span className={styles.previewLabel}>
                        {t("cardForm.linkedPreview.balance")}
                      </span>
                      <span className={styles.previewValue}>
                        {linkedAccount.walletBalance?.toLocaleString() ?? "—"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </fieldset>

            <div className={styles.formActions}>
              {isEditMode && (
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={() => {
                    setConfirmDeleteOpen(true);
                    setDeleteError(null);
                  }}
                  disabled={saving || deleting}
                >
                  {deleting ? t("cardForm.buttons.deleting") : t("cardForm.buttons.delete")}
                </button>
              )}
              <div className={styles.formActionsRight}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={navigateBack}
                  disabled={saving}
                >
                  {t("cardForm.buttons.cancel")}
                </button>
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={!canSubmit || saving}
                >
                  {saving ? primaryBusyLabel : primaryButtonLabel}
                </button>
              </div>
            </div>
          </form>
        )}
      </Card>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t("cardForm.buttons.delete")}
        message={t("cardForm.confirmDelete")}
        confirmLabel={deleting ? t("cardForm.buttons.deleting") : t("cardForm.buttons.delete")}
        cancelLabel={t("cardForm.buttons.cancel")}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
}

export default CardFormPage;
